/* eslint-disable no-underscore-dangle */
/**
 * The two things an operator does to a trip, shared by the Assignments table
 * and the trip details screen, so both behave (and read) the same:
 *
 *   DriverButton       "Assign driver", or the driver and car by name — a
 *                      tap opens AssignSheet (the caller renders the sheet)
 *   AcknowledgeButton  shown while the latest version of the job is not yet
 *                      confirmed as seen
 *   useAcknowledge     the acknowledge call, its busy state and its errors
 *   assignedNotice     the toast once a driver has been saved
 */
import React, { useCallback, useState } from 'react';
import { acknowledgeOperatorTrip } from '../../../Services/OperatorPortalApi';
import { CheckIcon } from '../Components/Icons';
import {
  driverText, needsAcknowledgement, operatorErrorMessage, vehicleText,
} from './operatorFormat';

export function DriverButton({ trip, onAssign }) {
  const cancelled = trip.status === 'Cancelled';

  if (!trip.driver) {
    if (cancelled) return <span className="pp-op-muted">—</span>;
    return (
      <button type="button" className="pp-btn pp-btn--primary pp-op-assign" onClick={() => onAssign(trip)}>
        Assign driver
      </button>
    );
  }

  return (
    <button
      type="button"
      className="pp-op-driver"
      onClick={() => onAssign(trip)}
      disabled={cancelled}
      aria-label={`Change driver and vehicle for ${trip.booking_reference}`}
    >
      <span className="pp-op-driver__name">{driverText(trip)}</span>
      <span className="pp-op-driver__car">{vehicleText(trip)}</span>
    </button>
  );
}

export function AcknowledgeButton({ trip, busy, onAcknowledge }) {
  if (!needsAcknowledgement(trip)) return null;
  return (
    <button
      type="button"
      className="pp-btn pp-btn--outline pp-op-ack"
      onClick={() => onAcknowledge(trip)}
      disabled={busy}
    >
      <CheckIcon size={16} width={2.5} />
      {busy ? 'Acknowledging…' : 'Acknowledge'}
    </button>
  );
}

/**
 * `acknowledge(trip)` confirms the operator has seen the job. On success
 * `onAcknowledged(trip, acknowledged)` gets the API's flag; on failure
 * `onError(message)` gets an English sentence. `acknowledging` is the id on
 * the wire, or null.
 */
export function useAcknowledge({ onAcknowledged, onError }) {
  const [acknowledging, setAcknowledging] = useState(null);

  const acknowledge = useCallback(async (trip) => {
    const id = trip._id;
    setAcknowledging(id);
    onError('');
    try {
      const updated = await acknowledgeOperatorTrip(id);
      onAcknowledged(trip, updated?.acknowledged ?? true);
    } catch (failure) {
      onError(operatorErrorMessage(failure, {
        404: `There is nothing to acknowledge on ${trip.booking_reference}.`,
      }));
    } finally {
      setAcknowledging(null);
    }
  }, [onAcknowledged, onError]);

  return { acknowledge, acknowledging };
}

/** The toast once AssignSheet has saved. */
export const assignedNotice = (trip) => `${trip?.booking_reference}: driver saved, the customer has been notified`;
